import type { BackendGiftListSeller } from "@/lib/mappers/backend-gift-list-schema";
import type { HostAttendance } from "./host-panel-types";

/**
 * Who attends the list, over the real contract (Requirement 3.4 to 3.6).
 *
 * The backend gives the seller's name and e-mail, nothing else — no phone, no store
 * (`GiftListSellerSummaryOutput`). Everything around those two facts is product copy (D5), the
 * same copy the approved prototype wrote, and the contact channel is the **real** Concierge's
 * WhatsApp (decision 5, 2026-09-16): the number already in production on the homepage
 * (`help-modal.tsx`), never the prototype's fictional test numbers.
 *
 * Pure, so the composition in `host-panel-adapter.ts` stays a one-liner and this file is
 * testable without a session.
 */

/** `+55 11 4003-9000`, the Concierge's WhatsApp, as `wa.me` wants it. */
export const CONCIERGE_WHATSAPP_NUMBER = "551140039000";

const CONCIERGE_WHATSAPP_DISPLAY = "(11) 4003-9000";
const CONCIERGE_EMAIL = "atendimento@taniabulhoes.com.br";
const SUPPORT_PHOTO = "https://static.taniabulhoes.com.br/5_fb2e74187d.jpg";

/** Fallback when the seller record carries no name: neutral, never "null null". */
const NAMELESS_SELLER = "Sua vendedora Tania Bulhões";

function whatsappHref(text: string): string {
  return `https://wa.me/${CONCIERGE_WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
}

const CONCIERGE_FACTS: HostAttendance["facts"] = [
  { label: "Horário", value: "Segunda a quinta, das 8h às 18h; sexta, das 8h às 17h" },
  { label: "Alcance", value: "Todas as lojas e o site" },
];

function sellerName(seller: BackendGiftListSeller): { full: string; first: string } {
  const first = (seller.firstName ?? "").trim();
  const last = (seller.lastName ?? "").trim();
  const full = `${first} ${last}`.trim();

  return full.length > 0 ? { full, first: first || full } : { full: NAMELESS_SELLER, first: "" };
}

function withSeller(seller: BackendGiftListSeller): HostAttendance {
  const name = sellerName(seller);
  const contactHelp = whatsappHref("Olá, preciso de ajuda com a minha lista.");

  return {
    hasAssignedSeller: true,
    name: name.full,
    firstName: name.first,
    role: "Vendedora administradora da sua lista",
    inlineRole: "a consultora de vendas que administra a sua lista",
    text: "Ela acompanha a sua lista do começo ao fim: curadoria das peças, dúvidas dos convidados, presentes recebidos e resgate do seu crédito na loja.",
    actionLabel: name.first.length > 0 ? `Falar com ${name.first}` : "Falar com a sua vendedora",
    shortLabel: "Falar com a Consultora",
    whatsappHref: contactHelp,
    collapseLabel: "Atendimento Tania Bulhões",
    facts: CONCIERGE_FACTS,
    contacts: [
      { label: "WhatsApp", value: CONCIERGE_WHATSAPP_DISPLAY, href: contactHelp },
      {
        label: "E-mail",
        value: seller.email,
        href: `mailto:${seller.email}?subject=${encodeURIComponent("Minha lista de presentes")}`,
      },
    ],
    support: {
      title: name.first.length > 0 ? `${name.first} · sua vendedora` : "Sua vendedora",
      text: "A sua lista está nas mãos de uma vendedora dedicada. Resgate de crédito, ajustes na curadoria, dúvidas sobre um pedido: ela acompanha você em cada etapa.",
      photoUrl: SUPPORT_PHOTO,
    },
  };
}

function withConcierge(): HostAttendance {
  const contactHelp = whatsappHref("Olá, preciso de ajuda com a minha lista.");

  return {
    hasAssignedSeller: false,
    name: "Concierge Tania Bulhões",
    firstName: "",
    role: "Atendimento dedicado à sua lista",
    inlineRole: "a nossa Concierge",
    /*
     * The line used to end with "Se preferir alguém dedicado em uma loja, é só pedir", which
     * existed to introduce the "Pedir um vendedor dedicado" action. The action was removed on
     * 2026-10-08, and an invitation with nothing to press is worse than no invitation.
     */
    text: "Sem vendedor vinculado, a Concierge cuida de tudo: curadoria das peças, dúvidas dos convidados, presentes recebidos e resgate do seu crédito.",
    actionLabel: "Falar com a Concierge",
    shortLabel: "Falar com a Concierge",
    whatsappHref: contactHelp,
    collapseLabel: "Atendimento Concierge",
    facts: CONCIERGE_FACTS,
    contacts: [
      { label: "WhatsApp", value: CONCIERGE_WHATSAPP_DISPLAY, href: contactHelp },
      {
        label: "E-mail",
        value: CONCIERGE_EMAIL,
        href: `mailto:${CONCIERGE_EMAIL}?subject=${encodeURIComponent("Minha lista de presentes")}`,
      },
    ],
    support: {
      title: "Atendimento Concierge",
      text: "Uma consultora da nossa Concierge acompanha você em cada etapa: resgate de crédito, ajustes na curadoria e dúvidas sobre um pedido, em horário comercial.",
      photoUrl: SUPPORT_PHOTO,
    },
  };
}

/** `seller` is the expanded record from `GET /gift-lists/me/{id}`; null means no one is assigned. */
export function buildAttendance(seller: BackendGiftListSeller | null): HostAttendance {
  return seller === null ? withConcierge() : withSeller(seller);
}
